import React, { useState } from 'react';
import { Box, Button, CircularProgress, Link, Popover, Stack, Typography } from '@mui/material';
import { User } from '@phosphor-icons/react';
import { getIsMobileApp } from '../../utils/mobileApp';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { clearLinkedUserId } from '../../utils/persistentUserId';
import { clearUserCookie } from '../../utils/userCookie';

// ─── Shared auth button ───────────────────────────────────────────────────────

const MobileAppAuthButton: React.FC = () => {
  const { isAuthenticated: auth0Authenticated, isLoading, email: auth0Email, login, logout: auth0Logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const history = useHistory();

  const isAuthenticated = auth0Authenticated;
  const email = auth0Email;

  const logout = () => {
    clearLinkedUserId();
    clearUserCookie();
    if (auth0Authenticated) {
      auth0Logout();
    } else {
      window.location.reload();
    }
  };

  const handleIconClick = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(anchorEl ? null : e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleLogout = () => { handleClose(); logout(); };

  if (isLoading) return <CircularProgress size={18} sx={{ color: 'rgba(255,255,255,0.5)' }} />;

  if (!isAuthenticated) {
    return (
      <Button
        onClick={() => login()}
        sx={{ color: 'rgba(242,242,243,0.7)', fontSize: '12px', fontFamily: 'Inter', textTransform: 'none', padding: '4px 8px', minWidth: 'auto' }}
      >
        Log in
      </Button>
    );
  }

  return (
    <>
      <Box onClick={handleIconClick} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: 'rgba(242,242,243,0.7)' }}>
        <User size={24} weight="thin" />
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { backgroundColor: '#1C1C1E', borderRadius: '12px' } }}
      >
        <Box p={2} minWidth={200} sx={{ fontFamily: 'Inter' }}>
          <Typography sx={{ fontSize: '14px', fontFamily: 'Inter', color: 'rgba(242,242,243,0.5)', mb: 1 }}>{email}</Typography>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            onClick={handleLogout}
            sx={{ mb: 1, fontFamily: 'Inter', fontSize: '14px', textTransform: 'none', color: 'rgba(242,242,243,0.85)', borderColor: 'rgba(242,242,243,0.2)', '&:hover': { borderColor: 'rgba(242,242,243,0.4)' } }}
          >
            Sign Out
          </Button>
          <Button
            fullWidth
            variant="text"
            size="small"
            onClick={() => { handleClose(); history.push('/account'); }}
            sx={{ mb: 1.5, fontFamily: 'Inter', fontSize: '14px', textTransform: 'none', color: 'rgba(242,242,243,0.85)' }}
          >
            My Account
          </Button>
          <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.12)', pt: 0.5, display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 1.5 }}>
            <Link
              href="https://www.stokastic.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontSize: '11px', fontFamily: 'Inter', color: 'rgba(242,242,243,0.4)', py: 1.5, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Privacy Policy
            </Link>
            <Typography sx={{ fontSize: '11px', color: 'rgba(242,242,243,0.2)' }}>|</Typography>
            <Link
              href="https://www.stokastic.com/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontSize: '11px', fontFamily: 'Inter', color: 'rgba(242,242,243,0.4)', py: 1.5, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Terms of Service
            </Link>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

// ─── Mobile app header ────────────────────────────────────────────────────────

interface MobileAppHeaderProps {
  title: string;
}

const MobileAppHeader: React.FC<MobileAppHeaderProps> = ({ title }) => {
  if (!getIsMobileApp()) return null;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        position: 'static',
        top: 0,
        zIndex: 1200,
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)',
        pb: 0,
        px: 2,
        flexShrink: 0,
        backgroundColor: 'transparent',
      }}
    >
      {/* Title */}
      <Box
        sx={{
          fontFamily: 'Inter',
          fontWeight: 700,
          fontSize: '22px',
          color: '#F2F2F3',
          letterSpacing: '-0.5px',
          flex: 1,
          mr: 2,
        }}
      >
        {title}
      </Box>

      {/* User icon */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <MobileAppAuthButton />
      </Box>
    </Stack>
  );
};

export default MobileAppHeader;
